import React, { useCallback, useEffect, useState } from "react";
import { Button, DrawerBody, DrawerHeader, DrawerHeaderTitle, MessageBar, MessageBarBody, OverlayDrawer, Radio, RadioGroup, Spinner, Text } from "@fluentui/react-components";
import { Copy20Regular, Dismiss24Regular, Video20Regular } from "@fluentui/react-icons";
import { cancelMeeting, getMeeting, meetingLink, onMeetingFrame, rsvp } from "../spurt/meetings";
import type { MeetingView, Rsvp } from "../spurt/meetings";
import { useChat } from "../spurt/chatStore";
import { errText } from "./meetingErrors";
import { badgeOf, copyText, fmtWhen, isHostRole } from "./meetingFormat";
import { openMeeting } from "./meetingUi";
import InviteesSection, { RSVP_TEXT } from "./InviteesSection";
import SettingsSection from "./SettingsSection";
import AttendanceSection from "./AttendanceSection";
import RecordingsSection from "./RecordingsSection";
import MinutesPanel from "./MinutesPanel";
import StatusChip from "./StatusChip";
import css from "./Meetings.module.css";

export interface MeetingDetailsProps {
  /** the row that was clicked: shown at once while the full meeting loads */
  summary: MeetingView;
  onClose: () => void;
  /** something changed: the list behind should reload */
  onChanged: () => void;
}

const ANSWERS: Exclude<Rsvp, "none">[] = ["accepted", "tentative", "declined"];
const ANSWER_LABEL: Record<(typeof ANSWERS)[number], string> = { accepted: "Accept", tentative: "Maybe", declined: "Decline" };

const MeetingDetails: React.FC<MeetingDetailsProps> = ({ summary, onClose, onChanged }) => {
  const users = useChat((s) => s.users);
  const [m, setM] = useState<MeetingView>(summary);
  const [loadError, setLoadError] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [scope, setScope] = useState<"single" | "series">("single");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const nameOf = useCallback((id: string): string => users[id]?.name || id, [users]);

  const reload = useCallback(async () => {
    try {
      setM(await getMeeting(summary.id));
      setLoadError("");
    } catch (e) {
      setLoadError(errText(e, "load this meeting"));
    }
  }, [summary.id]);

  useEffect(() => {
    void reload();
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    const off = onMeetingFrame((f) => {
      if (!/^meeting\.(updated|started|ended|canceled)$/.test(f.t)) return;
      const id = f.d.meetingId;
      if (id === summary.id || (summary.seriesId && id === summary.seriesId)) void reload();
    });
    return () => {
      window.clearInterval(t);
      off();
    };
  }, [reload, summary.id, summary.seriesId]);

  const host = isHostRole(m.myRole);
  const over = m.status === "ended" || m.status === "canceled";
  const series = !!(m.recurring || m.seriesId);
  const badge = badgeOf(m, now);
  const updated = (next: MeetingView) => {
    setM(next);
    onChanged();
  };

  const copy = async () => {
    let url = m.joinUrl;
    try {
      if (!url) url = (await meetingLink(m.id)).joinUrl;
    } catch (e) {
      setMsg({ ok: false, text: errText(e, "get the link") });
      return;
    }
    setMsg((await copyText(url)) ? { ok: true, text: "Link copied." } : { ok: false, text: "Couldn't copy the link. Select it and copy it by hand." });
  };

  const answer = async (a: Exclude<Rsvp, "none">) => {
    setBusy(true);
    setMsg(null);
    try {
      updated(await rsvp(m.id, a));
      setMsg({ ok: true, text: `Your answer: ${RSVP_TEXT[a].toLowerCase()}.` });
    } catch (e) {
      setMsg({ ok: false, text: errText(e, "answer this invitation") });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const sc = series ? scope : "single";
      await cancelMeeting(sc === "series" ? (m.seriesId ?? m.id) : m.id, sc);
      onChanged();
      onClose();
    } catch (e) {
      setMsg({ ok: false, text: errText(e, "cancel this meeting") });
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const canRsvp = !over && !host && (m.myRsvp !== undefined || m.myRole === "attendee");

  return (
    <OverlayDrawer open position="end" onOpenChange={(_, d) => !d.open && onClose()} style={{ width: "min(640px, 100vw)", maxWidth: "100vw" }}>
      <DrawerHeader>
        <DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Close details" icon={<Dismiss24Regular />} onClick={onClose} />}>{m.title}</DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={css.stack} aria-label="Meeting details">
          <div className={css.row}>
            <StatusChip badge={badge} />
            {m.recurring || m.seriesId ? <Text className={css.muted}>Repeats</Text> : null}
          </div>

          {loadError && (
            <MessageBar intent="warning" role="alert">
              <MessageBarBody>{loadError}</MessageBarBody>
            </MessageBar>
          )}
          {msg && (
            <MessageBar intent={msg.ok ? "success" : "error"} role={msg.ok ? "status" : "alert"}>
              <MessageBarBody>{msg.text}</MessageBarBody>
            </MessageBar>
          )}

          <section className={css.section} aria-label="When and where">
            <h3 className={css.h3}>When and where</h3>
            <Text block>{fmtWhen(m.startMs, m.endMs, m.timeZone)}{m.timeZone ? ` (${m.timeZone})` : ""}</Text>
            <Text block className={css.muted}>Online, in Spurt. Organised by {m.organizerName || nameOf(m.organizerId)}.</Text>
            {m.joinUrl && (
              <Text block className={css.breakAll}>
                {m.joinUrl}
              </Text>
            )}
            {m.code && <Text block className={css.muted}>Code: {m.code}</Text>}
            {host && m.passcode && <Text block className={css.muted}>Passcode: {m.passcode}</Text>}
            <div className={css.row}>
              {!over && (
                <Button appearance="primary" icon={<Video20Regular />} onClick={() => openMeeting({ meetingId: m.id })}>
                  Join
                </Button>
              )}
              <Button icon={<Copy20Regular />} onClick={() => void copy()}>
                Copy link
              </Button>
            </div>
          </section>

          {m.description && (
            <section className={css.section}>
              <h3 className={css.h3}>Description</h3>
              <Text className={css.pre}>{m.description}</Text>
            </section>
          )}

          {(m.agenda ?? []).length > 0 && (
            <section className={css.section} aria-label="Agenda">
              <h3 className={css.h3}>Agenda</h3>
              <ol className={css.list}>
                {(m.agenda ?? []).map((a, i) => (
                  <li key={i}>
                    <strong>{a.title}</strong>
                    {a.minutes ? <span className={css.muted}> · {a.minutes} min</span> : null}
                    {a.presenter ? <span className={css.muted}> · {a.presenter}</span> : null}
                    {a.notes ? <div className={css.muted}>{a.notes}</div> : null}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {canRsvp && (
            <section className={css.section} aria-label="Your answer">
              <h3 className={css.h3}>Will you attend?</h3>
              <div className={css.row}>
                {ANSWERS.map((a) => (
                  <Button key={a} appearance={m.myRsvp === a ? "primary" : "secondary"} aria-pressed={m.myRsvp === a} disabled={busy} onClick={() => void answer(a)}>
                    {ANSWER_LABEL[a]}
                  </Button>
                ))}
              </div>
            </section>
          )}

          <InviteesSection meeting={m} host={host} users={users} onUpdated={updated} />

          {host && <SettingsSection meeting={m} onUpdated={updated} />}

          {host && m.status !== "scheduled" && <AttendanceSection meetingId={m.id} title={m.title} nameOf={nameOf} />}

          {(m.status === "ended" || m.status === "live" || !!m.recordingId) && <RecordingsSection meetingId={m.id} />}

          {m.status === "ended" && (
            <section className={css.section} aria-label="Minutes">
              <h3 className={css.h3}>Minutes</h3>
              <MinutesPanel meetingId={m.id} isHost={host} />
            </section>
          )}

          {host && !over && (
            <section className={css.section} aria-label="Cancel meeting">
              <h3 className={css.h3}>Cancel meeting</h3>
              {!confirming ? (
                <div className={css.row}>
                  <Button onClick={() => setConfirming(true)}>Cancel meeting</Button>
                </div>
              ) : (
                <div className={css.stack} role="group" aria-label="Confirm cancel">
                  <Text>Everyone invited will be told it was cancelled. This can't be undone.</Text>
                  {series && (
                    <RadioGroup value={scope} onChange={(_, d) => setScope(d.value as "single" | "series")}>
                      <Radio value="single" label="Only this meeting" />
                      <Radio value="series" label="The whole series" />
                    </RadioGroup>
                  )}
                  <div className={css.row}>
                    <Button appearance="primary" className={css.danger} disabled={busy} icon={busy ? <Spinner size="tiny" /> : undefined} onClick={() => void cancel()}>
                      Yes, cancel it
                    </Button>
                    <Button disabled={busy} onClick={() => setConfirming(false)}>
                      Keep it
                    </Button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </DrawerBody>
    </OverlayDrawer>
  );
};

export default MeetingDetails;
